import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Search, Phone, Mail, MapPin, Trash2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomers() {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`, { headers });
      setCustomers(res.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const deleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Delete customer "${customerName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/customers/${customerId}`, { headers });
      setCustomers(prev => prev.filter(c => c.id !== customerId));
      toast.success('Customer deleted');
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.phone.includes(searchQuery) ||
    (customer.email || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout title="Customers" subtitle="View and manage your customer database">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Customers" subtitle="View and manage your customer database">
      <div data-testid="admin-customers">
        {/* Search */}
        <div className="mb-6 flex justify-between items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-card border-white/10 text-white h-12"
              data-testid="customer-search"
            />
          </div>
          <p className="text-gray-400 text-sm ml-4">{filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}</p>
        </div>

        {/* Customers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.map(customer => (
            <div
              key={customer.id}
              className="bg-card rounded-xl border border-white/10 p-6 group"
              data-testid={`customer-card-${customer.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-fire-500/20 flex items-center justify-center">
                    <span className="text-fire-500 font-bold text-lg">{customer.name?.[0]?.toUpperCase() || '?'}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{customer.name}</h3>
                    <p className="text-gray-400 text-sm">{customer.total_orders} order{customer.total_orders !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                  onClick={() => deleteCustomer(customer.id, customer.name)}
                  data-testid={`delete-customer-${customer.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-gray-400">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span className="text-white text-sm">{customer.phone || '-'}</span>
                </div>
                {customer.email && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="text-white text-sm truncate">{customer.email}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-start gap-2 text-gray-400">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span className="text-white text-sm">{customer.address}</span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Total Spent</span>
                  <span className="text-fire-500 font-bold">₦{customer.total_spent.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCustomers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No customers found
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
